import type { CanvasPoint, OffsetSourceContour, PolylineLikeShape, Shape } from './types';

const clonePoint = (point: CanvasPoint): CanvasPoint => ({ x: point.x, y: point.y });

const getDistance = (pointA: CanvasPoint, pointB: CanvasPoint) =>
  Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);

const getDistanceToSegment = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return getDistance(point, start);
  }

  const projection =
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared;
  const clamped = Math.max(0, Math.min(1, projection));

  return getDistance(point, {
    x: start.x + clamped * dx,
    y: start.y + clamped * dy,
  });
};

const isPointInsideClosedPolyline = (point: CanvasPoint, points: number[]) => {
  let inside = false;

  for (
    let currentIndex = 0, previousIndex = points.length - 2;
    currentIndex < points.length;
    previousIndex = currentIndex, currentIndex += 2
  ) {
    const currentX = points[currentIndex];
    const currentY = points[currentIndex + 1];
    const previousX = points[previousIndex];
    const previousY = points[previousIndex + 1];

    if (currentX === undefined || currentY === undefined || previousX === undefined || previousY === undefined) {
      continue;
    }

    const intersects =
      currentY > point.y !== previousY > point.y &&
      point.x <
        ((previousX - currentX) * (point.y - currentY)) / (previousY - currentY) + currentX;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const getPolylineVertices = (points: number[]) => {
  const vertices: CanvasPoint[] = [];

  for (let index = 0; index < points.length - 1; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    if (x === undefined || y === undefined) continue;
    const nextVertex = { x, y };
    const previousVertex = vertices[vertices.length - 1];

    if (
      previousVertex &&
      Math.abs(previousVertex.x - nextVertex.x) <= 0.0001 &&
      Math.abs(previousVertex.y - nextVertex.y) <= 0.0001
    ) {
      continue;
    }

    vertices.push(nextVertex);
  }

  return vertices;
};

const getPolylineSignedArea = (points: number[]) => {
  let area = 0;

  for (let index = 0; index < points.length; index += 2) {
    const nextIndex = (index + 2) % points.length;
    const x1 = points[index];
    const y1 = points[index + 1];
    const x2 = points[nextIndex];
    const y2 = points[nextIndex + 1];
    if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) continue;
    area += x1 * y2 - x2 * y1;
  }

  return area / 2;
};

const getLineIntersection = (
  pointA: CanvasPoint,
  directionA: CanvasPoint,
  pointB: CanvasPoint,
  directionB: CanvasPoint,
) => {
  const denominator = directionA.x * directionB.y - directionA.y * directionB.x;
  if (Math.abs(denominator) < 1e-9) return null;

  const deltaX = pointB.x - pointA.x;
  const deltaY = pointB.y - pointA.y;
  const factor = (deltaX * directionB.y - deltaY * directionB.x) / denominator;

  return {
    x: pointA.x + directionA.x * factor,
    y: pointA.y + directionA.y * factor,
  };
};

export const getOffsetPolylinePoints = (
  shape: PolylineLikeShape,
  offsetDistance: number,
) => {
  const vertices = getPolylineVertices(shape.points);

  if (vertices.length < 2) return null;

  const segmentCount = shape.closed ? vertices.length : vertices.length - 1;
  const offsetSegments: Array<{
    start: CanvasPoint;
    end: CanvasPoint;
    direction: CanvasPoint;
  }> = [];

  for (let index = 0; index < segmentCount; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    if (!start || !end) return null;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) return null;

    const normal = {
      x: (-dy / length) * offsetDistance,
      y: (dx / length) * offsetDistance,
    };

    offsetSegments.push({
      start: {
        x: start.x + normal.x,
        y: start.y + normal.y,
      },
      end: {
        x: end.x + normal.x,
        y: end.y + normal.y,
      },
      direction: {
        x: dx / length,
        y: dy / length,
      },
    });
  }

  const offsetVertices: CanvasPoint[] = [];
  const pushOffsetVertex = (vertex: CanvasPoint) => {
    const previousVertex = offsetVertices[offsetVertices.length - 1];
    if (
      previousVertex &&
      Math.abs(previousVertex.x - vertex.x) <= 0.0001 &&
      Math.abs(previousVertex.y - vertex.y) <= 0.0001
    ) {
      return;
    }
    offsetVertices.push(vertex);
  };

  if (!shape.closed) {
    const firstSegment = offsetSegments[0];
    const lastSegment = offsetSegments[offsetSegments.length - 1];
    if (!firstSegment || !lastSegment) return null;

    pushOffsetVertex(firstSegment.start);

    for (let index = 1; index < offsetSegments.length; index += 1) {
      const previous = offsetSegments[index - 1];
      const current = offsetSegments[index];
      if (!previous || !current) continue;
      const intersection = getLineIntersection(
        previous.start,
        previous.direction,
        current.start,
        current.direction,
      );

      pushOffsetVertex(intersection ?? current.start);
    }

    pushOffsetVertex(lastSegment.end);
  } else {
    for (let index = 0; index < offsetSegments.length; index += 1) {
      const previous = offsetSegments[(index - 1 + offsetSegments.length) % offsetSegments.length];
      const current = offsetSegments[index];
      if (!previous || !current) continue;
      const intersection = getLineIntersection(
        previous.start,
        previous.direction,
        current.start,
        current.direction,
      );

      pushOffsetVertex(intersection ?? current.start);
    }

    if (offsetVertices.length >= 2) {
      const firstVertex = offsetVertices[0];
      const lastVertex = offsetVertices[offsetVertices.length - 1];
      if (
        firstVertex &&
        lastVertex &&
        Math.abs(firstVertex.x - lastVertex.x) <= 0.0001 &&
        Math.abs(firstVertex.y - lastVertex.y) <= 0.0001
      ) {
        offsetVertices.pop();
      }
    }
  }

  return offsetVertices.flatMap((vertex) => [vertex.x, vertex.y]);
};

export const buildOffsetSourceShape = (
  shape: Shape,
  sourceContour: OffsetSourceContour,
): Shape | null => {
  if (sourceContour.kind === 'outer') {
    if (shape.kind === 'arc') {
      return null;
    }
    return shape;
  }

  if (shape.kind === 'circle' || shape.kind === 'arc') {
    return null;
  }

  if (sourceContour.kind === 'polygon-hole') {
    const hole = shape.holes?.[sourceContour.holeIndex];
    if (!hole) return null;

    return {
      ...shape,
      points: [...hole],
      holes: undefined,
      circleHoles: undefined,
      closed: true,
      kind: 'polyline',
    };
  }

  const circleHole = shape.circleHoles?.[sourceContour.holeIndex];
  if (!circleHole) return null;

  return {
    id: `${shape.id}-circle-hole-${sourceContour.holeIndex}`,
    kind: 'circle',
    closed: true,
    center: clonePoint(circleHole.center),
    radius: circleHole.radius,
    layer: shape.layer,
    name: `${shape.name} hole`,
    strokeColor: shape.strokeColor,
    strokeWidth: shape.strokeWidth,
    entityStyleKind: 'circle',
  };
};

export const getOffsetDistanceForPointer = (
  shape: Shape,
  pointer: CanvasPoint,
  absoluteDistance: number,
) => {
  if (shape.kind === 'circle') {
    return getDistance(pointer, shape.center) >= shape.radius ? absoluteDistance : -absoluteDistance;
  }

  if (shape.kind === 'arc') {
    return absoluteDistance;
  }

  if (shape.closed) {
    const areaSign = getPolylineSignedArea(shape.points) >= 0 ? 1 : -1;
    return isPointInsideClosedPolyline(pointer, shape.points)
      ? absoluteDistance * areaSign
      : -absoluteDistance * areaSign;
  }

  let nearestDistance = Number.POSITIVE_INFINITY;
  let signedSide = 1;

  for (let index = 0; index <= shape.points.length - 4; index += 2) {
    const startX = shape.points[index];
    const startY = shape.points[index + 1];
    const endX = shape.points[index + 2];
    const endY = shape.points[index + 3];
    if (startX === undefined || startY === undefined || endX === undefined || endY === undefined) continue;
    const start = { x: startX, y: startY };
    const end = { x: endX, y: endY };
    const distance = getDistanceToSegment(pointer, start, end);
    if (distance >= nearestDistance) continue;

    nearestDistance = distance;
    const cross =
      (end.x - start.x) * (pointer.y - start.y) - (end.y - start.y) * (pointer.x - start.x);
    signedSide = cross >= 0 ? 1 : -1;
  }

  return absoluteDistance * signedSide;
};

export const buildOffsetShapePreview = (
  shape: Shape,
  pointer: CanvasPoint,
  distance: number,
  sourceContour: OffsetSourceContour = { kind: 'outer' },
) => {
  const absoluteDistance = Math.max(0, distance);
  if (absoluteDistance <= 0) return null;

  const sourceShape = buildOffsetSourceShape(shape, sourceContour);
  if (!sourceShape) return null;
  const signedDistance = getOffsetDistanceForPointer(sourceShape, pointer, absoluteDistance);

  if (sourceShape.kind === 'circle') {
    const nextRadius = sourceShape.radius + signedDistance;
    if (nextRadius <= 0) return null;

    return {
      kind: 'circle' as const,
      center: sourceShape.center,
      radius: nextRadius,
    };
  }

  if (sourceShape.kind === 'arc') {
    return null;
  }

  const nextPoints = getOffsetPolylinePoints(sourceShape, signedDistance);
  if (!nextPoints) return null;

  return {
    kind: 'polyline' as const,
    points: nextPoints,
    closed: sourceShape.closed,
  };
};
